import { Flex } from '@radix-ui/themes';
import { CommonCallout } from 'components/common/callouts';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTabs } from 'components/common/tabs';
import { MachineInspectionStandaloneHoC } from 'components/machine/dialogs/inspection';
import { StereoCheckDialogHoC } from 'components/machine/dialogs/stereo-check/stereo-check';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { MachineContext } from 'contexts/machine.context';
import { t } from 'i18next';
import { IStreamProps } from 'interfaces/i-streaming';
import { UserRole } from 'lib_ts/enums/auth.enums';
import { VISION_ENABLED_HW_VERSIONS } from 'lib_ts/enums/machine.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useContext, useState } from 'react';
import { useAuthStore } from 'stores/use-auth-store';

enum MachineInspectTabKey {
  BallInserter = 'Ball Inserter',
  VisionPlayback = 'Vision Playback',
}

const BallInserterStream = (props: IStreamProps) => (
  <MachineInspectionStandaloneHoC
    machineID={props.machineID}
    hwVersion={props.hwVersion}
    mode={props.mode}
  />
);

const VisionStream = (props: IStreamProps) => (
  <StereoCheckDialogHoC
    machineID={props.machineID}
    hwVersion={props.hwVersion}
    mode={props.mode}
    hideCalibration
    asStandalone
  />
);

export const MachineInspect = () => {
  const current = useAuthStore((s) => s.current);

  const { isActive, machine: connectedMachine } = useContext(MachineContext);
  const machine = useMachinesPortalStore((s) => s.machine);

  const [activeTab, setActiveTab] = useState<MachineInspectTabKey>(
    MachineInspectTabKey.BallInserter
  );

  if (!machine) {
    return null;
  }

  if (!machine.connected) {
    return (
      <Flex direction="column" pt="2">
        <CommonCallout
          text={t('admin.machine-is-currently-offline').toString()}
          color={RADIX.COLOR.DANGER}
        />
      </Flex>
    );
  }

  // 'other' whenever inspecting a different machine
  const streamMode =
    isActive && machine.machineID === connectedMachine.machineID
      ? 'self'
      : 'other';

  const hasVision = VISION_ENABLED_HW_VERSIONS.includes(
    machine.hardware_version
  );

  const isAdmin = current.role === UserRole.admin;

  return (
    <ErrorBoundary identifier="MachineDetailsErrorsTab">
      <Flex direction="column">
        {hasVision && isAdmin ? (
          <CommonTabs
            tabs={[
              {
                label: t('admin.ball-inserter'),
                value: MachineInspectTabKey.BallInserter,
                content: (
                  <BallInserterStream
                    machineID={machine.machineID}
                    hwVersion={machine.hardware_version}
                    mode={streamMode}
                  />
                ),
              },
              {
                label: t('admin.vision-playback'),
                value: MachineInspectTabKey.VisionPlayback,
                content: (
                  <VisionStream
                    machineID={machine.machineID}
                    hwVersion={machine.hardware_version}
                    mode={streamMode}
                  />
                ),
              },
            ]}
            value={activeTab}
            onValueChange={(value) =>
              setActiveTab(value as MachineInspectTabKey)
            }
            gap="1"
          />
        ) : (
          <BallInserterStream
            machineID={machine.machineID}
            hwVersion={machine.hardware_version}
            mode={streamMode}
          />
        )}
      </Flex>
    </ErrorBoundary>
  );
};
